import React, { useState, useRef, useCallback, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Interactive before/after slider.
 *
 * The source `composite` is a single image where the left half is the "avant"
 * state and the right half is the "après" state (square composite, each half
 * portrait 1:2). We render two layers, each scaled to 200% width and
 * translated so that one shows the left half and the other shows the right
 * half. A clip-path on the top layer reveals the bottom layer based on the
 * draggable handle position.
 */
const BeforeAfterSlider = ({ composite, alt = '', initial = 50, className = '', testId }) => {
  const [position, setPosition] = useState(initial);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);

  const updatePosition = useCallback((clientX) => {
    const node = containerRef.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    const ratio = (clientX - rect.left) / rect.width;
    setPosition(Math.max(0, Math.min(100, ratio * 100)));
  }, []);

  // Global listeners while dragging
  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e) => {
      const x = e.touches ? e.touches[0].clientX : e.clientX;
      updatePosition(x);
    };
    const onUp = () => setIsDragging(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: true });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
  }, [isDragging, updatePosition]);

  const handleStart = (clientX) => {
    setIsDragging(true);
    updatePosition(clientX);
  };

  return (
    <div
      ref={containerRef}
      data-testid={testId}
      className={`relative aspect-square overflow-hidden bg-slate-100 select-none touch-none ${className}`}
      onMouseDown={(e) => handleStart(e.clientX)}
      onTouchStart={(e) => handleStart(e.touches[0].clientX)}
      style={{ cursor: isDragging ? 'grabbing' : 'ew-resize' }}
    >
      {/* AFTER layer — right half of composite, full bleed */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <img
          src={composite}
          alt={`${alt} après`}
          draggable={false}
          className="absolute top-0 left-0 h-full max-w-none"
          style={{ width: '200%', transform: 'translateX(-50%)' }}
        />
      </div>

      {/* BEFORE layer — left half, clipped by slider position */}
      <div
        className="absolute inset-0 overflow-hidden pointer-events-none"
        style={{ clipPath: `inset(0 ${100 - position}% 0 0)` }}
      >
        <img
          src={composite}
          alt={`${alt} avant`}
          draggable={false}
          className="absolute top-0 left-0 h-full max-w-none"
          style={{ width: '200%', transform: 'translateX(0%)' }}
        />
      </div>

      {/* Labels */}
      <div className="absolute top-4 left-4 z-10 bg-black/75 backdrop-blur text-white text-[11px] font-bold px-3 py-1.5 rounded-full uppercase tracking-widest pointer-events-none shadow-lg">
        Avant
      </div>
      <div className="absolute top-4 right-4 z-10 bg-[hsl(207,90%,54%)] text-white text-[11px] font-bold px-3 py-1.5 rounded-full uppercase tracking-widest pointer-events-none shadow-lg">
        Après
      </div>

      {/* Vertical divider line */}
      <div
        className="absolute top-0 bottom-0 w-[3px] bg-white shadow-[0_0_20px_rgba(0,0,0,0.5)] pointer-events-none z-20"
        style={{ left: `${position}%`, transform: 'translateX(-50%)' }}
      >
        {/* Handle */}
        <div
          data-testid={testId ? `${testId}-handle` : undefined}
          className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-white flex items-center justify-center shadow-2xl ring-4 ring-white/30 transition-transform ${
            isDragging ? 'scale-110' : 'scale-100'
          }`}
          style={{ pointerEvents: 'none' }}
        >
          <ChevronLeft className="w-4 h-4 text-gray-800 -mr-1" />
          <ChevronRight className="w-4 h-4 text-gray-800 -ml-1" />
        </div>
      </div>

      {/* Hint pulse */}
      {!isDragging && position > 45 && position < 55 && (
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-white/40 animate-ping pointer-events-none z-10"
          style={{ left: `${position}%` }}
        />
      )}
    </div>
  );
};

export default BeforeAfterSlider;
